// Damage photos for maintenance tickets: pick up to 3 (camera on mobile), preview, and upload after the ticket exists.
import React, { useRef, useState } from 'react';
import { ArrowRight, Camera, X } from 'lucide-react';
import { API_URL, uploadFile } from './_shared';

export const MAX_TICKET_PHOTOS = 3;
const OK_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export const uploadTicketPhotos = async (ticketId, files, phase = 'before') => {
  let ticket = null;
  const errors = [];
  for (const f of files) {
    try { ticket = await uploadFile(`/api/admin/housekeeping/tickets/${ticketId}/photos?phase=${phase}`, f); }
    catch (e) { errors.push(`${f.name}: ${e.message}`); }
  }
  return { ticket, errors };
};

export const TicketPhotoPicker = ({ files, onChange, testId = 'ticket-photos', hint = 'damage photos — maintenance sees them before walking up' }) => {
  const inputRef = useRef(null);
  const [err, setErr] = useState('');
  const add = (list) => {
    const next = [...files];
    setErr('');
    for (const f of Array.from(list || [])) {
      if (!OK_TYPES.includes(f.type)) { setErr('Only JPEG, PNG or WEBP photos'); continue; }
      if (f.size > 8 * 1024 * 1024) { setErr('Photo too large (max 8 MB)'); continue; }
      if (next.length >= MAX_TICKET_PHOTOS) { setErr(`Max ${MAX_TICKET_PHOTOS} photos`); break; }
      next.push(f);
    }
    onChange(next);
    if (inputRef.current) inputRef.current.value = '';
  };
  const thumbs = files.map((f, i) => (
    <div key={`${f.name}-${i}`} className="relative w-16 h-16 rounded-lg overflow-hidden border bg-slate-100" data-testid={`${testId}-thumb-${i}`}>
      <img src={URL.createObjectURL(f)} alt="" className="w-full h-full object-cover" />
      <button type="button" onClick={() => onChange(files.filter((_, j) => j !== i))} className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full p-0.5" aria-label="Remove photo" data-testid={`${testId}-remove-${i}`}><X className="w-3 h-3" /></button>
    </div>
  ));
  return (
    <div className="space-y-1" data-testid={testId}>
      <div className="flex flex-wrap gap-2 items-center">
        {thumbs}
        {files.length < MAX_TICKET_PHOTOS && (
          <button type="button" onClick={() => inputRef.current?.click()} className="w-16 h-16 rounded-lg border-2 border-dashed border-slate-300 hover:border-orange-400 text-slate-500 hover:text-orange-600 flex flex-col items-center justify-center text-[10px] gap-0.5" data-testid={`${testId}-add`}>
            <Camera className="w-5 h-5" /> {files.length ? 'Add' : 'Photo'}
          </button>
        )}
        <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" capture="environment" multiple className="hidden" onChange={(e) => add(e.target.files)} data-testid={`${testId}-input`} />
        <span className="text-[11px] text-slate-500">{files.length}/{MAX_TICKET_PHOTOS} {hint}</span>
      </div>
      {err && <div className="text-xs text-red-600" data-testid={`${testId}-error`}>{err}</div>}
    </div>
  );
};

export const photosByPhase = (photos) => {
  const before = [];
  const after = [];
  for (const p of photos || []) ((p.phase || 'before') === 'after' ? after : before).push(p.url);
  return { before, after };
};

export const TicketPhotoStrip = ({ photos, size = 'w-10 h-10', testId = 'ticket-photo', phase }) => {
  const [open, setOpen] = useState(null);
  const urls = (photos || []).filter((p) => !phase || typeof p === 'string' || (p.phase || 'before') === phase).map((p) => (typeof p === 'string' ? p : p.url)).filter(Boolean);
  if (!urls.length) return null;
  const thumbs = urls.map((u, i) => (
    <button key={u} type="button" onClick={() => setOpen(u)} className={`${size} rounded-md overflow-hidden border hover:ring-2 hover:ring-orange-400`} data-testid={`${testId}-${i}`}>
      <img src={`${API_URL}${u}`} alt="Damage photo" className="w-full h-full object-cover" loading="lazy" />
    </button>
  ));
  return (
    <>
      <div className="flex gap-1 flex-wrap">{thumbs}</div>
      {open && (
        <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4" onClick={() => setOpen(null)} data-testid={`${testId}-lightbox`}>
          <img src={`${API_URL}${open}`} alt="Damage photo" className="max-w-full max-h-full rounded-lg shadow-2xl" />
          <button type="button" onClick={() => setOpen(null)} className="absolute top-4 right-4 text-white bg-black/50 rounded-full p-2" aria-label="Close"><X className="w-5 h-5" /></button>
        </div>
      )}
    </>
  );
};

// Owner view: damage photos → repair photos side by side.
export const BeforeAfterStrip = ({ before, after, testId = 'ticket-ba' }) => {
  if (!before?.length && !after?.length) return null;
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={testId}>
      {before?.length > 0 && <div><div className="text-[10px] uppercase text-red-700 font-semibold">Before</div><TicketPhotoStrip photos={before} testId={`${testId}-before`} /></div>}
      {before?.length > 0 && after?.length > 0 && <ArrowRight className="w-4 h-4 text-slate-400 mt-3" />}
      {after?.length > 0 && <div><div className="text-[10px] uppercase text-emerald-700 font-semibold">After · repaired</div><TicketPhotoStrip photos={after} testId={`${testId}-after`} /></div>}
    </div>
  );
};
